import { Trans } from "@lingui/macro";
import { IApiSmpGptAgent } from "@smp/api";
import { UiButton, UiDialogResponsive, UiTypography } from "@smp/ui";
import { FC, useCallback, useMemo, useState } from "react";

import { useDialog } from "@/hooks/use_dialog";
import { useQueryGptAgentList } from "@/queries";
import { dialogAdvisorConfig } from "@/screens/app/components/dialogs/dialog_advisor/config";

import classes from "./dialog_advisor.module.css";
import { AgentEdit, IAgentEditProps } from "./screens/agent_edit";
import { AgentList, IAgentListProps } from "./screens/agent_list";
import {
  AgentPostSettings,
  IAgentPostSettingsProps,
} from "./screens/agent_post_settings";
import { CreateForm, ICreateFormProps } from "./screens/create_form";
import { PostsGenerator } from "./screens/posts_generator";

export interface IDialogAdvisorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DialogAdvisor: FC<IDialogAdvisorProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [view, setView] = useState<
    | "agent_list"
    | "agent_create"
    | "agent_edit"
    | "agent_post_settings"
    | "generate_posts"
  >("agent_list");
  const [currentAgent, setCurrentAgent] = useState<IApiSmpGptAgent>();
  const [agentCreateStep, setAgentCreateStep] = useState<number | undefined>();
  const [isCreateFormSubmitting, setIsCreateFormSubmitting] =
    useState<boolean>(false);
  const [updatedAgent, setUpdatedAgent] = useState<IApiSmpGptAgent>();

  const { data: agentListData, isLoading: isAgentListLoading } =
    useQueryGptAgentList({
      enabled: isOpen,
    });

  const {
    isOpen: isDialogLimitReachedOpen,
    open: openDialogLimitReached,
    close: closeDialogLimitReached,
  } = useDialog();

  const {
    isOpen: isDialogCloseConfirmOpen,
    open: openDialogCloseConfirm,
    close: closeDialogCloseConfirm,
  } = useDialog();

  const agents = useMemo(
    () => agentListData?.pages?.flatMap(({ results }) => results) || [],
    [agentListData?.pages],
  );

  const currentAgentIndex = useMemo(
    () =>
      currentAgent
        ? agents.findIndex((agent) => agent.id === currentAgent.id)
        : -1,
    [agents, currentAgent],
  );

  const title = useMemo(() => {
    if (view === "agent_edit" && currentAgent) {
      return (
        <>
          <Trans>Preset</Trans> {currentAgentIndex + 1}
        </>
      );
    }

    return <Trans>AI SMM assistant</Trans>;
  }, [currentAgent, currentAgentIndex, view]);

  const handleCreateAgentStart = useCallback<
    IAgentListProps["onCreate"]
  >(() => {
    if (agents.length >= dialogAdvisorConfig.agent_limit) {
      openDialogLimitReached();
      return;
    }

    setView("agent_create");
  }, [agents.length, openDialogLimitReached]);

  const handleCreateAgent = useCallback<ICreateFormProps["onSuccess"]>(
    (agent) => {
      setCurrentAgent(agent);
      setUpdatedAgent(undefined);
      setView("generate_posts");
    },
    [],
  );

  const handleEditAgent = useCallback<IAgentListProps["onEdit"]>((agent) => {
    setCurrentAgent(agent);
    setUpdatedAgent(undefined);
    setView("agent_edit");
  }, []);

  const handleDuplicateAgent = useCallback<IAgentEditProps["onDuplicate"]>(
    (callback) => {
      if (agents.length >= dialogAdvisorConfig.agent_limit) {
        openDialogLimitReached();
        return;
      }

      callback();
    },
    [agents.length, openDialogLimitReached],
  );

  const handleOpenPostSettings = useCallback<IAgentListProps["onGenerate"]>(
    (agent) => {
      setCurrentAgent(agent);
      setUpdatedAgent(undefined);
      setView("agent_post_settings");
    },
    [],
  );

  const handleGeneratePosts = useCallback<
    IAgentPostSettingsProps["onGenerate"]
  >((agent) => {
    setCurrentAgent(agent);
    setUpdatedAgent(undefined);
    setView("generate_posts");
  }, []);

  const handleCloseDialog = useCallback(() => {
    if (
      view === "generate_posts" ||
      (view === "agent_create" &&
        ((agentCreateStep || 0) >= 1 || isCreateFormSubmitting))
    ) {
      openDialogCloseConfirm();
      return;
    }

    if (view === "agent_edit") {
      setView("agent_list");
      return;
    }

    onClose();
  }, [
    agentCreateStep,
    isCreateFormSubmitting,
    onClose,
    openDialogCloseConfirm,
    view,
  ]);

  return (
    <UiDialogResponsive
      open={isOpen}
      onClose={handleCloseDialog}
      isLoading={isAgentListLoading}
      title={title}
      fullWidth
      maxWidth={720}
      isSmallPadding
      classes={{
        paper: classes.paper,
      }}
      TransitionProps={{
        onExited: () => {
          setView("agent_list");
          closeDialogCloseConfirm();
          closeDialogLimitReached();
          setUpdatedAgent(undefined);
        },
      }}
    >
      {view === "agent_list" && (
        <AgentList
          value={agents}
          updatedAgentId={updatedAgent?.id}
          onCreate={handleCreateAgentStart}
          onEdit={handleEditAgent}
          onGenerate={handleOpenPostSettings}
        />
      )}

      {view === "agent_create" && (
        <CreateForm
          onSuccess={handleCreateAgent}
          onChangeStep={setAgentCreateStep}
          onChangeState={(state) =>
            setIsCreateFormSubmitting(state === "submitting")
          }
        />
      )}

      {view === "generate_posts" && currentAgent && (
        <PostsGenerator agent={currentAgent} onSuccess={onSuccess} />
      )}

      {view === "agent_edit" && currentAgent && (
        <AgentEdit
          agent={currentAgent}
          agentIdx={currentAgentIndex}
          onDelete={() => setView("agent_list")}
          onSuccess={() => setView("agent_list")}
          onUpdate={setUpdatedAgent}
          onDuplicate={handleDuplicateAgent}
        />
      )}

      {view === "agent_post_settings" && currentAgent && (
        <AgentPostSettings
          agent={currentAgent}
          agentIdx={currentAgentIndex}
          onCancel={() => setView("agent_list")}
          onGenerate={handleGeneratePosts}
        />
      )}

      <UiDialogResponsive
        open={isDialogLimitReachedOpen}
        onClose={closeDialogLimitReached}
        title={<Trans>There are no free slots</Trans>}
        isSmallPadding
        maxWidth={464}
        fullWidth
        // actionsLeft={[
        //   <UiButton color="paper" size="large" onClick={closeDialogLimitReached}>
        //     <Trans>Cancel</Trans>
        //   </UiButton>,
        // ]}
        // actionsRight={[
        //   <UiButton variant="contained" size="large" disabled>
        //     <Trans>Increase the number of slots</Trans>
        //   </UiButton>,
        // ]}
        actionsRight={[
          <UiButton size="large" onClick={closeDialogLimitReached}>
            <Trans>Ok</Trans>
          </UiButton>,
        ]}
      >
        <UiTypography variant="body2">
          <Trans>
            You used {agents.length} of {dialogAdvisorConfig.agent_limit}{" "}
            available slots for setting AI SMM assistant.
          </Trans>
        </UiTypography>

        {/*<UiTypography variant="body2">*/}
        {/*  <Trans>You can increase the available number of slots.</Trans>*/}
        {/*</UiTypography>*/}
      </UiDialogResponsive>

      <UiDialogResponsive
        open={isDialogCloseConfirmOpen}
        onClose={closeDialogCloseConfirm}
        title={<Trans>Attention, generation will be stopped.</Trans>}
        isSmallPadding
        fullWidth
        actionsRight={[
          <UiButton size="large" variant="outlined" onClick={onClose}>
            <Trans>Cancel</Trans>
          </UiButton>,
          <UiButton
            size="large"
            variant="contained"
            onClick={closeDialogCloseConfirm}
          >
            <Trans>Continue</Trans>
          </UiButton>,
        ]}
      >
        <UiTypography variant="body2">
          <Trans>
            If you want to continue generation using AI SMM Assistant, click
            "Continue".
          </Trans>
        </UiTypography>
      </UiDialogResponsive>
    </UiDialogResponsive>
  );
};
